import { Button, colors, List, ListItem, ListItemIcon, ListItemText, Theme, Typography } from '@material-ui/core';
import LinearProgress from '@material-ui/core/LinearProgress';
import FileCopyIcon from '@material-ui/icons/FileCopy';
import { createStyles, makeStyles } from '@material-ui/styles';
import clsx from 'clsx';
import React, { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import FileUploadIcon from "../assets/undraw_add_file2_gvbb.svg";
import bytesToSize from './bytesToSize';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {},
    dropZone: {
      border: `1px dashed black`,
      padding: 6,
      outline: 'none',
      display: 'flex',
      justifyContent: 'center',
      flexWrap: 'wrap',
      alignItems: 'center',
      '&:hover': {
        backgroundColor: colors.grey[50],
        opacity: 0.5,
        cursor: 'pointer',
      },
    },
    dragActive: {
      backgroundColor: colors.grey[50],
      opacity: 0.5,
    },
    image: {
      width: 130,
    },
    info: {
      marginTop: 1,
    },
    list: {
      maxHeight: 320,
    },
    actions: {
      marginTop: 2,
      display: 'flex',
      justifyContent: 'flex-end',
      '& > * + *': {
        marginLeft: 2,
      },
    },
  })
);
type FilesDropzoneProps = {
  className?: string;
  onUploadFile: (file: any) => void;
  onRemoveFile?: () => void;
  uploadError?: string;
  setUploadError: (error: string) => void;
  fileLoadPercentage: number;
  onFileSelected: () => void;
  isUploading?: boolean;
};
const FilesDropzone: React.FC<FilesDropzoneProps> = ({
  fileLoadPercentage,
  setUploadError,
  onFileSelected,
  uploadError,
  className,
  onUploadFile,
  isUploading,
  onRemoveFile,
  ...rest
}) => {
  const classes = useStyles();
  const [files, setFiles] = useState([]);

  const handleDrop = useCallback(
    (acceptedFiles) => {
      setFiles((prevFiles) => [].concat(acceptedFiles));
      setUploadError('');
      onFileSelected();
    },
    [onFileSelected, setUploadError]
  );

  const handleRemoveAll = () => {
    setFiles([]);
    if (onRemoveFile) {
      onRemoveFile();
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: handleDrop,
    multiple: false,
  });

  return (
    <div {...rest} className={clsx(classes.root, className)}>
      <div
        className={clsx({
          [classes.dropZone]: true,
          [classes.dragActive]: isDragActive,
        })}
        {...getRootProps()}
      >
        <input {...getInputProps()} />
        <div>
          <img alt="Select file" className={classes.image} src={FileUploadIcon} />
        </div>
        <div style={{marginLeft: '51px'}}>
          <Typography gutterBottom style={{color:'#000000'}} variant="h3">
            Select your CSV file
          </Typography>
          <Typography style={{color:'#000000'}} className={classes.info} variant="body1">
            Drop files here or click browse thorough your machine
          </Typography>
        </div>
      </div>
      {files.length > 0 && (
        <>
          <List className={classes.list}>
            {files.map((file: any, i) => (
              <>
                <ListItem divider={i < files.length - 1} key={i}>
                  <ListItemIcon>
                    <FileCopyIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography>
                        {file.name}
                        <Typography style={{ color: 'red' }}>{uploadError}</Typography>
                      </Typography>
                    }
                    primaryTypographyProps={{ variant: 'h5' }}
                    secondary={bytesToSize(file.size)}
                  />
                </ListItem>
                <LinearProgress variant="determinate" value={fileLoadPercentage} />
              </>
            ))}
          </List>

          <div className={classes.actions}>
            <Button disabled={isUploading} onClick={handleRemoveAll} size="small">
              Remove
            </Button>
            <Button
              disabled={isUploading}
              color="secondary"
              size="small"
              variant="contained"
              onClick={() => onUploadFile(files[0])}
            >
              Upload file
            </Button>
          </div>
        </>
      )}
    </div>
  );
};

export default FilesDropzone;
